import pick from 'lodash/pick.js';
import uuid from 'uuid/v4.js';

import { handleResponseError } from '@/Helpers.js';
import Model from '@/models/Model.js';
import TermSchedule from '@/models/term-schedule/TermSchedule.js';
import TermSchedulePeriodCourse from '@/models/term-schedule/TermSchedulePeriodCourse.js';

export default class TermSchedulePeriod extends Model {
    static entity = 'term_schedule_period';

    static modelName = 'TermSchedulePeriod';

    static primaryKey = 'uuid';

    static fields() {
        return {
            id: this.number(null).nullable(),
            term_schedule_id: this.attr(null),
            uuid: this.attr(() => uuid()),
            title: this.string(''),
            weeks: this.number(null).nullable(),
            order: this.number(0),
            period_order: this.number(0).nullable(),
            type: this.attr(null),
            period_start_date: this.attr(null),
            period_end_date: this.attr(null),

            ...super.fields(),

            courses: this.hasMany(TermSchedulePeriodCourse, 'term_schedule_period_id', 'id'),
            parent: this.belongsTo(TermSchedule, 'term_schedule_id', 'id'),
        };
    }

    // Defaults
    get modelName() {
        return this.modelName;
    }

    static searchable() {
        return ['title'];
    }

    static async addNewPeriod(termSchedule, type) {
        const index = termSchedule.periods.length;
        const titleIndex = termSchedule.periods.filter(item => item.type === 'period').length + 1;

        const newPeriod = await new TermSchedulePeriod({
            title: type === 'period' ? `Term ${titleIndex}` : 'Term Break',
            order: index,
            period_order: type === 'period' ? titleIndex : null,
            type,
            term_schedule_id: termSchedule.id,
            weeks: 1,
        }).$save();

        await this.storeNewPeriod(newPeriod);

        return newPeriod;
    }

    // API - CREATE
    static async storeNewPeriod(period) {
        const requiredFields = pick(period, ['uuid', 'title', 'order', 'period_order', 'weeks', 'type']);
        try {
            const { response } = await TermSchedulePeriod.api().post(
                `/api/term-schedules/${period.term_schedule_id}/periods`,
                {
                    ...requiredFields,
                }
            );

            if (response.status >= 200 && response.status < 300) {
                this.store().dispatch('AutoSaveEventBus/updateSubscription', {
                    subscription: 'UPDATE_COMPONENT_KEY',
                    data: {
                        action: 'termTemplateUpdated',
                    },
                });

                return true;
            }
        } catch (e) {
            handleResponseError(e);
        }

        return false;
    }

    static async sync(termTemplatePeriod, data) {
        const endpoint = `/api/term-schedules/${termTemplatePeriod.term_schedule_id}/periods/${termTemplatePeriod.id}`;

        try {
            const { response } = await this.api().put(endpoint, data);

            if (response.status >= 200 && response.status < 300) {
                this.store().dispatch('AutoSaveEventBus/updateSubscription', {
                    subscription: 'UPDATE_COMPONENT_KEY',
                    data: {
                        action: 'termPeriodUpdated',
                        termPeriodId: termTemplatePeriod.id,
                    },
                });

                this.store().dispatch('AutoSaveEventBus/updateSubscription', {
                    subscription: 'UPDATE_COMPONENT_KEY',
                    data: {
                        action: 'termTemplateUpdated',
                    },
                });

                return response;
            }
        } catch (e) {
            handleResponseError(e);
        }

        return false;
    }

    // API - DELETE
    static async remove(termSchedulePeriod) {
        const endpoint = `/api/term-schedules/${termSchedulePeriod.term_schedule_id}/periods/${termSchedulePeriod.id}`;
        try {
            const { response } = await this.api().delete(endpoint);

            if (response.status >= 200 && response.status < 300) {
                await this.delete(termSchedulePeriod.uuid);

                this.store().dispatch('AutoSaveEventBus/updateSubscription', {
                    subscription: 'UPDATE_COMPONENT_KEY',
                    data: {
                        action: 'termTemplateUpdated',
                    },
                });

                return response;
            }
        } catch (e) {
            handleResponseError(e);
        }

        return false;
    }

    static async reorder(termId, updatedPeriods) {
        const endpoint = `/api/term-schedules/${termId}/periods/reorder`;
        try {
            const { response } = await this.api().post(endpoint, {
                periods: updatedPeriods,
            });

            if (response.status >= 200 && response.status < 300) {
                await this.store().dispatch('AutoSaveEventBus/updateSubscription', {
                    subscription: 'UPDATE_COMPONENT_KEY',
                    data: {
                        action: 'termTemplateUpdated',
                    },
                });

                return response;
            }
        } catch (e) {
            handleResponseError(e);
        }

        return false;
    }

    get isBreak() {
        return this.type === 'break';
    }

    get isCourse() {
        return this.type === 'course';
    }

    get isPeriod() {
        return this.type === 'period';
    }

    get calendar_weeks() {
        if (this.type === 'break') {
            return this.weeks;
        }
        return this.courses.reduce((acc, course) => acc + course.duration, 0);
    }

    get formattedCourses() {
        return (
            this.courses?.map(course => ({
                ...course,
                duration_rate:
                    course.start_week === 1
                        ? course.duration + course.start_week - 1 + this.breaksDuration
                        : course.duration + course.start_week + this.breaksDuration,
            })) ?? []
        );
    }

    get maxDuration() {
        let maxWeek = 0;
        this.formattedCourses.forEach(course => {
            const splits = this.splitCourse(course);
            splits.forEach(split => {
                maxWeek = Math.max(maxWeek, split.start_week + split.duration - 1);
            });
        });
        return maxWeek;
    }

    splitCourse(course) {
        const splits = [];
        let currentStart = course.start_week;
        let remainingDuration = course.duration;
        const breakPeriods = this.breaks.sort((a, b) => a.start_week - b.start_week);

        breakPeriods.forEach(breakPeriod => {
            if (breakPeriod.start_week >= currentStart && breakPeriod.start_week < currentStart + remainingDuration) {
                const firstPartDuration = breakPeriod.start_week - currentStart;
                if (firstPartDuration > 0) {
                    splits.push({
                        ...course,
                        start_week: currentStart,
                        duration: firstPartDuration,
                    });
                    remainingDuration -= firstPartDuration;
                }
                currentStart = breakPeriod.start_week + breakPeriod.duration;
            }
        });

        if (remainingDuration > 0) {
            splits.push({
                ...course,
                start_week: currentStart,
                duration: remainingDuration,
            });
        }

        return splits;
    }

    get calculated_calendar_weeks() {
        return this.type === 'break' ? this.weeks : this.maxDuration;
    }

    get breaks() {
        return this.courses.filter(course => course.type === 'break');
    }

    get actual_instruction_weeks() {
        const breakWeeks = this.breaks.reduce((weeks, course) => weeks + course.duration, 0);
        return this.maxDuration - breakWeeks;
    }
}
